// Which unit fields the quantity section shows; the arithmetic is POST /units/convert.

export const WEIGHT_UNITS = Object.freeze(['MT', 'kg', 'lbs']);
export const VOLUME_UNITS = Object.freeze(['m³', 'm³/15°C', 'gal']);
export const ALL_UNITS = Object.freeze([...WEIGHT_UNITS, ...VOLUME_UNITS]);

// The server's unitMappingMiddleware already returns display forms, but material.
const ALIASES = Object.freeze({
  CUBIC_METER: 'm³',
  CUBIC_METER_15C: 'm³/15°C',
  m3: 'm³',
  'm3/15°C': 'm³/15°C',
  lb: 'lbs',
  LBS: 'lbs',
  Lbs: 'lbs',
  MT: 'MT',
  mt: 'MT',
  KG: 'kg',
});

export const normalizeUnit = (unit) => {
  if (!unit || typeof unit !== 'string') return '';
  const trimmed = unit.trim();
  return ALIASES[trimmed] ?? trimmed;
};

export const isWeightUnit = (unit) => WEIGHT_UNITS.includes(normalizeUnit(unit));
export const isVolumeUnit = (unit) => VOLUME_UNITS.includes(normalizeUnit(unit));

export const sameCategory = (left, right) =>
  (isWeightUnit(left) && isWeightUnit(right)) || (isVolumeUnit(left) && isVolumeUnit(right));

const FIXED_FACTORS = Object.freeze({
  'MT>kg': 1000,
  'MT>lbs': 2204.62,
  'kg>MT': 0.001,
  'kg>lbs': 2.20462,
  'lbs>MT': 0.000453592,
  'lbs>kg': 0.453592,
  'm³>gal': 264.172,
  'm³>m³/15°C': 1,
  'm³/15°C>m³': 1,
  'm³/15°C>gal': 264.172,
  'gal>m³': 0.00378541,
  'gal>m³/15°C': 0.00378541,
});

export const getFixedConversionFactor = (from, to) =>
  FIXED_FACTORS[`${normalizeUnit(from)}>${normalizeUnit(to)}`] ?? null;

// Cross-category pairs are the only ones the user has to supply a number for.
export const needsDensity = (from, to) => {
  const fromUnit = normalizeUnit(from);
  const toUnit = normalizeUnit(to);
  if (!fromUnit || !toUnit || fromUnit === toUnit) return false;
  return !sameCategory(fromUnit, toUnit);
};

// Every unit except the one or two already on screen.
export const getTrackingUnitOptions = (declarationUnit, workingUnit) => {
  const taken = new Set([normalizeUnit(declarationUnit), normalizeUnit(workingUnit)]);
  return ALL_UNITS.filter((unit) => !taken.has(unit));
};

// The server's own rule (helpers/materialUnitEngine.js) is the same exclusion, nothing more.
export const getSaveableTrackingUnitOptions = getTrackingUnitOptions;

/**
 * Which of the two units is the weight one and which the volume one, for a
 * "Density (MT per m³)" label. Null for same-category pairs, which use a factor instead.
 */
export const getDensityUnits = (from, to) => {
  if (!needsDensity(from, to)) return null;
  const fromUnit = normalizeUnit(from);
  const toUnit = normalizeUnit(to);

  return isWeightUnit(fromUnit)
    ? { weightUnit: fromUnit, volumeUnit: toUnit }
    : { weightUnit: toUnit, volumeUnit: fromUnit };
};
